import React, { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
  Drawer,
  useTheme,
  useMediaQuery,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import BurgerIcon from '@mui/icons-material/Menu';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

// CONSTANTS
import { WHITE } from '@/constants/colors';
import { isDevEnv } from '@/constants/common';

// HOOKS
import useActions from '@/hooks/useActions';
import { useAppSelector } from '@/hooks/redux';

// STORE
import { appSlice } from '@/store/slices/app.slice';
import { getPartner } from '@/store/selectors/auth';
import { getAppState } from '@/store/selectors/app';

// COMPONENTS
import NavList from '@/components/NavList';

// COMMON
import logoImg from '@/assets/images/logo-new.svg';
import logoWhiteImg from '@/assets/images/logo-new-white.svg';

// STYLES
import { LogoWrapper, Logo, BurgerButton, PartnersSelect, PoweredByContainer } from './styles';

const SideMenu = () => {
  const { t } = useTranslation();

  const theme = useTheme();
  const upLg = useMediaQuery(({ breakpoints }) => breakpoints.up('lg'));
  const partner = useAppSelector(getPartner);
  const { navigation, partners, dictionaryLoading } = useAppSelector(getAppState);

  const { switchDefaultPartner } = useActions(appSlice.actions);

  const [mobileOpen, setMobileOpen] = useState(false);
  const logo = theme.palette.secondary.main.toUpperCase() === WHITE ? logoImg : logoWhiteImg;
  const logoLink = navigation.find(item => item.type === 'navigation')?.route || '';

  const handleDrawerToggle = () => {
    if (!upLg) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleSwitchPartner = (event: SelectChangeEvent) => {
    const partnerId = Number(event.target.value);

    function onSuccess() {
      window.location.reload();
    }

    switchDefaultPartner({ partnerId, onSuccess });
  };

  const partnersContent = () => {
    if (partners.length <= 1) return null;

    return (
      <PartnersSelect className="partners-select">
        <InputLabel>
          {t('inputs.switchPartner')}&nbsp;
          <Tooltip
            title={t('inputs.switchPartnerHelpText')}
            slotProps={{ popper: { sx: { maxWidth: 200 } } }}
          >
            <InfoOutlineIcon className="cursor--pointer" />
          </Tooltip>
        </InputLabel>
        <Select value={partner.id} onChange={handleSwitchPartner} className="sizeSmall">
          {partners.map(item => (
            <MenuItem value={item.id} key={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </PartnersSelect>
    );
  };

  const partnerSwitcher = partners.length > 1 && isDevEnv ? partnersContent() : null;

  const drawer = (
    <>
      <LogoWrapper>
        <Link to={logoLink}>
          <Logo src={partner?.settings?.reporting_logo_overwrite || partner.logo || logo} />
        </Link>
      </LogoWrapper>
      {partnerSwitcher}
      <NavList data={navigation} loading={dictionaryLoading} />
      <PoweredByContainer className="powered-by">
        <div className="powered-by__text">{t('common.poweredBy')}</div>
        <div className="powered-by__logo">
          <img src={logo} alt="logo" />
        </div>
      </PoweredByContainer>
    </>
  );

  return (
    <>
      {!mobileOpen && !upLg && (
        <BurgerButton onClick={handleDrawerToggle}>
          <BurgerIcon />
        </BurgerButton>
      )}
      {!upLg ? (
        <Drawer
          variant="temporary"
          onClose={handleDrawerToggle}
          open={mobileOpen}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer variant="permanent" open>
          {drawer}
        </Drawer>
      )}
    </>
  );
};

const SideMenuComposed = React.memo(SideMenu);

export default SideMenuComposed;
